import React, { useContext } from 'react';

import { ReactComponent as PlusIcon } from 'assets/icons/Plus.svg';
import { ReactComponent as PaperPlaneIcon } from 'assets/icons/PaperPlane.svg';
import { ReactComponent as ThoughtsBubble } from 'assets/icons/ThoughtsBubble.svg';
import { ReactComponent as UnsaveIcon } from 'assets/icons/Unsave.svg';
import { ROUTE_ADD_TO_LIST, ROUTE_NICE_FIND, ROUTE_SELECT_CHAT_MODAL } from 'routes';
import useOpenModal from 'helpers/useOpenModal';
import useThoughtsModal from 'helpers/useThoughtsModal';

import { CardSchema, MessageType } from 'constants/graphqlTypes';

import withHapticFeedback from 'helpers/withHapticFeedback';
import { INavigation } from 'providers/helpers/models';
import { NavigationContext } from 'providers/helpers/contexts';
import { useProfile } from 'graphQL/profile/hooks';
import { useGotoMoveToList } from 'helpers/useContextMenu';
import getAuthUser from 'helpers/getAuthUser';
import { ReactionsProvider } from 'components/Reactions/hooks';
import { useDeleteSavedPicks } from 'graphQL/savedPicks/hooks';
import useToast from 'helpers/useToast';
import { MenuWrapper, TimeSince } from './styled';
import IconButton from './IconButton';

interface CardFooterMenuProps {
  card: CardSchema;
  color: string;
  isMyProfile?: boolean;
  isSavedPicksView?: boolean;
}

const CardFooterMenu: React.FC<CardFooterMenuProps> = ({ card, color, isMyProfile, isSavedPicksView }) => {
  const { userData } = useProfile();
  const { isScrolled } = useContext<INavigation>(NavigationContext);
  const { pickId } = card;
  const { userId } = getAuthUser();

  const pickIdOfLoggedUser = card?.picks?.items.find(({ userId: pickedUserId }) => pickedUserId === userId)?.id;

  const { externalSources, isPickedByMe, isSavedByMe } = card ?? {};
  const cardImage = card?.cardStyle?.thumbnailImageSet?.images?.[0]?.url ?? undefined;
  const cardColorValue = card?.cardStyle?.bgColor?.colorValue;
  const { sourceUrl, sourceDomainName } = externalSources?.items[0] ?? {};

  // Get the first parent card's ID as the listId
  const listId = card?.parentCards?.items?.[0]?.cardId;

  const niceFindLocationVariable = {
    pickId,
    referrerPickId: pickId || undefined,
    cardId: card.cardId,
    listId,
    card,
    cardImage,
    cardColorValue,
    sourceUrl: sourceUrl || undefined,
    sourceDomainName: sourceDomainName || undefined,
    userData,
  };

  const userPickSchemaRef = card?.extraData?.items?.find((data) => data?.user?.id === userId)?.id;

  const { toggle: goToMovePick } = useGotoMoveToList({
    pickId: pickIdOfLoggedUser ?? '',
    cardId: card.cardId ?? '',
    currentListId: card.cardId ?? '',
    userPickSchemaRef,
    disableListRedirect: true,
  });

  const goToNiceFind = useOpenModal(ROUTE_NICE_FIND, false, niceFindLocationVariable);

  const goToPickFlow = useOpenModal(ROUTE_ADD_TO_LIST, false, {
    pickId,
    cardId: card.cardId,
    isPickFlow: true,
    listId,
    isSavedPick: isSavedByMe && !isPickedByMe,
  });

  const handlePick = () => {
    // For saved picks that aren't picked yet, convert them to picks
    if (isSavedByMe && !isPickedByMe) {
      goToPickFlow();
    } else {
      // Allow picking (or re-picking) via Nice Find flow
      // Backend will create a new pick even if user has already picked this card
      goToNiceFind();
    }
  };

  const goToChatSelect = useOpenModal(ROUTE_SELECT_CHAT_MODAL, false, {
    entityId: pickId,
    messageType: MessageType.Pick,
    collectionId: null,
  });

  const openThoughts = useThoughtsModal({
    id: pickId || '',
    openFromCardDetailPage: true,
  });

  const { setToast } = useToast();
  const onUnsaveComplete = () => setToast({ isToastOpen: true, toastItemName: 'Unsaved', toastError: false });

  const { deleteSavedPicks: unsave, loading: isUnsaveLoading } = useDeleteSavedPicks([card.cardId], onUnsaveComplete);

  const handleUnsave = () => {
    if (isUnsaveLoading) return;
    unsave();
  };

  const getRelativeTime = (savedAt?: string | null): string | null => {
    if (!savedAt) return null;
    const savedMs = new Date(savedAt).getTime();
    if (Number.isNaN(savedMs)) return null;
    const now = Date.now();
    const diffMs = Math.max(0, now - savedMs);
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 14) return `${diffDays}d`;
    const diffWeeks = Math.floor(diffDays / 7);
    return `${diffWeeks}w`;
  };

  const timeSince = getRelativeTime((card as any).savedAt);

  return (
    <ReactionsProvider
      value={{
        isQuickReactionOpen: false,
        thoughtId: null,
        pickerId: card.user?.id,
        cardId: card.id,
        user: card.user,
        setThoughtId: () => {},
        setPickerId: () => {},
        setCardId: () => {},
        setUser: () => {},
      }}
    >
      {!isMyProfile && (
        <MenuWrapper>
          {isSavedPicksView && timeSince && <TimeSince aria-label={`Saved ${timeSince} ago`}>{timeSince}</TimeSince>}
          {isSavedPicksView && (
            <IconButton
              ariaLabel="Unsave"
              color={color}
              handleClick={(e) => {
                e.stopPropagation();
                withHapticFeedback(() => !isScrolled && handleUnsave())();
              }}
            >
              <UnsaveIcon />
            </IconButton>
          )}
          <IconButton
            ariaLabel="Add"
            color={color}
            handleClick={(e) => {
              e.stopPropagation();
              withHapticFeedback(() => !isScrolled && handlePick())();
            }}
          >
            <PlusIcon />
          </IconButton>
          <IconButton
            ariaLabel="Thoughts"
            color={color}
            handleClick={(e) => {
              e.stopPropagation();
              if (!isScrolled) {
                openThoughts();
              }
            }}
          >
            <ThoughtsBubble />
          </IconButton>
          <IconButton
            ariaLabel="Send"
            color={color}
            handleClick={(e) => {
              e.stopPropagation();
              if (!isScrolled) {
                goToChatSelect();
              }
            }}
          >
            <PaperPlaneIcon />
          </IconButton>
        </MenuWrapper>
      )}
    </ReactionsProvider>
  );
};

export default CardFooterMenu;
